import { format } from 'date-fns';

import { Button } from '@/components/Buttons';
import HabitatLink from '@/components/HabitatLink';
import { Video, VideoV2 } from '@/queries/media/types';
import { useSelector } from '@/redux/helper';
import { selectHasExplorerFeature } from '@/redux/selectors/users';
import { trackMP } from '@/shared/mp';

import HabitatProfileImageLink from './HabitatProfileImageLink';

interface HomeHighlightVideoInfoBarProps {
  highlight: Video | VideoV2;
  onNextHighlight?: () => void;
}

const HomeHighlightVideoInfoBar = ({ highlight, onNextHighlight }: HomeHighlightVideoInfoBarProps) => {
  const hasExplorerFeature = useSelector(selectHasExplorerFeature);
  const videoHabitat = 'habitat' in highlight ? highlight.habitat : (highlight as Video).habitatId;
  const type = 'videoType' in highlight ? highlight.videoType : highlight.type;
  const username = 'user' in highlight ? highlight.user?.username : (highlight as Video).username;
  const { publishedAt } = highlight;
  const habitat = useSelector((state) => state.allHabitats.find((h) => h._id === videoHabitat?._id));

  const trackVisitHabitatClick = () => {
    if (habitat) {
      trackMP('visit-habitat-link-clicked', {
        habitatId: habitat._id,
        entryPoint: 'highlight-info-bar',
      });
    }
  };

  return (
    <div className="relative z-10 bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.07)] mobile:px-[18px] mobile:py-[17px] desktop:flex desktop:items-center desktop:justify-between desktop:px-6 desktop:py-5">
      <div className="flex items-start gap-7 desktop:grow desktop:items-center desktop:gap-0">
        {habitat?.zoo && (
          <div className="shrink-0 pb-4">
            <HabitatProfileImageLink
              habitatId={habitat._id}
              slug={habitat.slug}
              zooSlug={habitat.zoo.slug}
              profileImage={habitat.profileImage}
              title={habitat.title}
              zooLogo={habitat.zoo.logo}
              zooName={habitat.zoo.name}
              entryPoint="highlight-info-bar"
            />
          </div>
        )}
        <div className="flex min-w-0 grow flex-col pl-1 desktop:pl-8 desktop:pr-2">
          {habitat?.zoo && (
            <HabitatLink
              productsPromptModalState={{ entryPoint: 'highlight-info-bar' }}
              habitatId={habitat._id}
              slug={habitat.slug}
              zooSlug={habitat.zoo.slug}
              className="preflight preflight-a text-xl font-medium leading-none text-grey-1 hover:text-grey-3"
              onClick={trackVisitHabitatClick}
            >
              {habitat.title}
            </HabitatLink>
          )}
          {habitat?.zoo && habitat.animal && (
            <p className="preflight preflight-p mt-[7px] text-xs font-bold uppercase leading-4 text-stone-500 desktop:text-[10px] desktop:leading-[11px]">
              {habitat.animal} AT {habitat.zoo.name}
            </p>
          )}
          <div className="mt-3 text-sm leading-[18px] desktop:text-base desktop:leading-[21px]">
            <span className="text-black" data-testid="highlight-title-info-bar-desktop">{`\u201C${
              highlight.title || 'Highlight'
            }\u201D taken by `}</span>
            {type === 'curated' ? (
              <span className="text-primary-green">@zoolife</span>
            ) : (
              <a
                href={`/u/${username || 'zoolife'}`}
                className="preflight preflight-a text-primary-green hover:text-secondary-green"
              >
                @{username || 'zoolife'}
              </a>
            )}
          </div>
          {publishedAt && <div className="text-grey-6 mobile:hidden">{format(new Date(publishedAt), 'd MMMM, haaa')}</div>}
        </div>
      </div>
      <div className="flex shrink-0 gap-2 mobile:hidden">
        {habitat?.zoo && (
          <HabitatLink
            productsPromptModalState={{ entryPoint: 'highlight-info-bar' }}
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className="preflight preflight-a flex items-center justify-center rounded-full border border-transparent bg-primary-green px-4 py-2 text-center text-sm font-medium text-white hover:bg-secondary-green"
            onClick={trackVisitHabitatClick}
          >
            Visit Habitat
          </HabitatLink>
        )}
        {!(type === 'clip' && !hasExplorerFeature) && onNextHighlight && (
          <Button variant="outline" onClick={onNextHighlight}>
            Next Clip
          </Button>
        )}
      </div>
      <div className="mt-3 flex gap-2.5 desktop:hidden">
        {habitat?.zoo && (
          <HabitatLink
            productsPromptModalState={{ entryPoint: 'highlight-info-bar' }}
            habitatId={habitat._id}
            slug={habitat.slug}
            zooSlug={habitat.zoo.slug}
            className="preflight preflight-a flex h-9 flex-1 items-center justify-center rounded-full border border-primary-green bg-primary-green text-[15px] font-medium text-white hover:bg-secondary-green"
            onClick={trackVisitHabitatClick}
          >
            Visit Habitat
          </HabitatLink>
        )}
        {!(type === 'clip' && !hasExplorerFeature) && onNextHighlight && (
          <button
            type="button"
            onClick={onNextHighlight}
            className="preflight preflight-button flex h-9 flex-1 items-center justify-center rounded-full border border-primary-green bg-white text-[15px] font-medium text-primary-green outline-none"
          >
            Next Clip
          </button>
        )}
      </div>
    </div>
  );
};

export default HomeHighlightVideoInfoBar;
